<template>
  <header
    class="header"
    :class="{ 'header--scrolled': isScrolled }"
  >
    <div class="header__locales">
      <label
        v-for="lang in locales"
        :key="lang"
        class="header__locale"
      >
        <input
          v-model="locale"
          type="radio"
          name="locale"
          :value="lang"
          class="header__radio"
          @change="persistLocale(lang)"
        />
        {{ lang }}
      </label>
    </div>
    <label class="header__theme">
      <input
        v-model="isLight"
        type="checkbox"
        name="theme"
        class="header__checkbox"
      />
      <Transition>
        <div
          v-if="isLight"
          key="sun"
          class="header__theme--sun"
        />
        <div
          v-else
          key="moon"
          class="header__theme--moon"
        />
      </Transition>
    </label>
  </header>
</template>

<script setup lang="ts">
import cookie from 'js-cookie';
import mixpanel from 'mixpanel-browser/src/loaders/loader-module-core';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTheme } from '@/composables/theme.ts';

const { availableLocales: locales, locale } = useI18n();
const { isLight } = useTheme();

const isScrolled = ref(window.scrollY > 60);

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
});

function handleScroll() {
  isScrolled.value = window.scrollY > 60;
}

function persistLocale(lang: string) {
  mixpanel.track('Locale', { locale: lang });
  cookie.set('locale', lang, { expires: 365 });
}
</script>

<style lang="scss" scoped>
@use '@/styles/screens';

.header {
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  position: fixed;
  transition: background-color 0.1s ease-in-out,
    border-color 0.3s ease-in-out,
    box-shadow 0.3s ease-in-out;
  top: 0;
  width: 100%;
  z-index: 2;

  @include screens.tablet {
    justify-content: flex-end;
  }
}

.header--scrolled {
  background-color: var(--color-background);
}

.dark .header--scrolled {
  border-bottom: 1px solid var(--color-border);
}

.light .header--scrolled {
  box-shadow: 0 2px 4px var(--color-border);
}

.header__locales {
  display: flex;
  gap: 8px;
}

.header__locale {
  cursor: pointer;
  transition: color 0.3s ease-in-out;

  &:hover {
    text-decoration: underline;
  }

  &:has(.header__radio:checked) {
    font-weight: bold;
    text-decoration: underline;
  }
}

.header__checkbox,
.header__radio {
  display: none;
}

.header__theme {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
}

.header__theme--moon,
.header__theme--sun {
  border-radius: 50%;
  transition: transform 0.3s ease-in-out;
  height: 100%;
  width: 100%;
}

.header__theme--moon {
  background-color: #808080;
  border: 3px solid #696969;
}

.header__theme--sun {
  background-color: #e0cc18;
  border: 3px solid #978c28;
}

.header__theme--moon.v-enter-from,
.header__theme--sun.v-enter-from,
.header__theme--moon.v-leave-from,
.header__theme--sun.v-leave-from {
  transform: scale(0);
}

.header__theme--moon.v-enter-to,
.header__theme--sun.v-enter-to {
  transform: scale(1);
}
</style>
